/*
  Quite a lot of ink spilled on this…
  https://github.com/vercel/next.js/discussions/12348#discussioncomment-131768

  It would seem that the Prompt from react-router-dom provides simply doesn't
  have an equivalent in Next.js so we have this bespoke option. 

  It seems like a solid enough solution, but let's keep an eye on it!
*/
import { useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/router';

let leaveConfirmed = false;

/**
 * See comment in @watershed/errors/ErrorRegistry.ts for
 * details on why this custom error class is necessary. It ensures that we don't
 * send these very unique exceptions to senry.
 */
export class NextNavigationCancellationError extends Error {
  name = 'NextNavigationCancellationError';
  code = 'NEXT_NAVIGATION_CANCELLATION';
  constructor(message?: string) {
    super(message);
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, NextNavigationCancellationError);
    }
    Object.setPrototypeOf(this, NextNavigationCancellationError.prototype);
  }
}

const useLeavePrevention = ({
  when,
  message,
  shouldInterceptClientRender = () => true,
}: {
  when: boolean;
  message: string;
  shouldInterceptClientRender?: (pathname: string) => boolean;
}) => {
  const router = useRouter();

  const handleWindowClose = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: any) => {
      e.preventDefault();
      e.returnValue = message;
      return e.returnValue;
    },
    [message]
  );

  // Use beforeunload to prevent closing the tab, refreshing the page or moving outside the Next app
  useEffect(() => {
    if (when) {
      window.addEventListener('beforeunload', handleWindowClose);
    } else {
      window.removeEventListener('beforeunload', handleWindowClose);
    }

    return () => {
      window.removeEventListener('beforeunload', handleWindowClose);
    };
  }, [when, handleWindowClose]);

  // Store latest callback in a ref so we don't have to add it as a dependency
  // to the effect.
  const shouldInterceptClientRenderRef = useRef(shouldInterceptClientRender);
  shouldInterceptClientRenderRef.current = shouldInterceptClientRender;

  // Use routeChangeStart to prevent navigation inside of the Next app
  // Uses a module variable to bypass the confirm, otherwise we would be in a loop
  const handleClientNavigation = useCallback(
    (pathname: string) => {
      if (leaveConfirmed) return;

      if (!shouldInterceptClientRenderRef.current(pathname)) return;

      if (window.confirm(message)) {
        leaveConfirmed = true;
      } else {
        router.events.emit('routeChangeError');
        throw new NextNavigationCancellationError();
      }
    },
    [message, router.events]
  );

  useEffect(() => {
    if (when) {
      router.events.on('routeChangeStart', handleClientNavigation);
    } else {
      router.events.off('routeChangeStart', handleClientNavigation);
    }

    return () => {
      router.events.off('routeChangeStart', handleClientNavigation);
    };
  }, [when, handleClientNavigation, router.events]);

  // Set the module variable to false on component mount
  // Note: This means you can't use this twice on the same page
  useEffect(() => {
    // TODO: This should probably move to a `useRef()`, so this is handled
    // locally per individual usage of this hook. But will need to test that
    // first.
    leaveConfirmed = false;
  }, []);
};

export default useLeavePrevention;
